const CART_KEY = 'cart';
const CART_BACKUP_KEY = 'cart_backup';
const CART_METADATA_KEY = 'cart_metadata';

/**
 * Enhanced cart local storage with backup and validation
 */

/**
 * Validate cart item structure
 * @param {Object} item - Cart item to validate
 * @returns {boolean} - Is valid cart item
 */
const isValidCartItem = (item) => {
  return (
    item &&
    typeof item === 'object' &&
    typeof item.id === 'string' &&
    typeof item.name === 'string' &&
    typeof item.price === 'number' &&
    item.price >= 0 &&
    typeof item.quantity === 'number' &&
    item.quantity > 0
  );
};

/**
 * Validate cart data structure
 * @param {Array} cartData - Cart data to validate
 * @returns {Array} - Cleaned cart data
 */
const validateCartData = (cartData) => {
  if (!Array.isArray(cartData)) {
    console.warn('🛒 Invalid cart data: not an array');
    return [];
  }

  const validItems = cartData.filter((item) => {
    const isValid = isValidCartItem(item);
    if (!isValid) {
      console.warn('🛒 Removing invalid cart item:', item);
    }
    return isValid;
  });

  return validItems;
};

/**
 * Create cart metadata
 * @param {Array} cartData - Cart data
 * @returns {Object} - Cart metadata
 */
const createCartMetadata = (cartData) => {
  return {
    timestamp: new Date().toISOString(),
    itemCount: cartData.length,
    totalQuantity: cartData.reduce((sum, item) => sum + (item.quantity || 1), 0),
    version: '2.0',
  };
};

/**
 * Save cart data with backup
 * @param {Array} cart - Cart data to save
 */
export function setLocalCart(cart) {
  try {
    const validCart = validateCartData(cart);
    const cartString = JSON.stringify(validCart);
    const metadata = createCartMetadata(validCart);

    // Get current cart for backup
    const currentCart = getLocalCart();
    if (currentCart.length > 0) {
      // Backup current cart before overwriting
      window.localStorage.setItem(CART_BACKUP_KEY, JSON.stringify(currentCart));
    }

    // Save new cart and metadata
    window.localStorage.setItem(CART_KEY, cartString);
    window.localStorage.setItem(CART_METADATA_KEY, JSON.stringify(metadata));

    if (process.env.NODE_ENV === 'development') {
      console.log('🛒 Cart saved to localStorage:', {
        items: validCart.length,
        totalQuantity: metadata.totalQuantity,
        hasBackup: currentCart.length > 0,
      });
    }
  } catch (error) {
    console.error('🛒 Failed to save cart to localStorage:', error);
    // Try to save a minimal version
    try {
      window.localStorage.setItem(CART_KEY, '[]');
    } catch (fallbackError) {
      console.error('🛒 Failed to save even empty cart:', fallbackError);
    }
  }
}

/**
 * Get cart data with automatic recovery
 * @returns {Array} - Cart data
 */
export function getLocalCart() {
  try {
    const cartString = window.localStorage.getItem(CART_KEY);

    if (!cartString) {
      // No cart data found, check for backup
      return recoverFromBackup();
    }

    const cartData = JSON.parse(cartString);
    const validCart = validateCartData(cartData);

    // If validation removed items, save the cleaned version
    if (validCart.length !== cartData.length) {
      console.warn('🛒 Cart data was corrupted, cleaned and re-saved');
      setLocalCart(validCart);
    }

    return validCart;
  } catch (error) {
    console.error('🛒 Failed to load cart from localStorage:', error);

    // Try to recover from backup
    const recoveredCart = recoverFromBackup();
    if (recoveredCart.length > 0) {
      console.log('🛒 Recovered cart from backup');
      setLocalCart(recoveredCart);
      return recoveredCart;
    }

    // Last resort: return empty cart
    return [];
  }
}

/**
 * Recover cart from backup
 * @returns {Array} - Recovered cart data
 */
function recoverFromBackup() {
  try {
    const backupString = window.localStorage.getItem(CART_BACKUP_KEY);
    if (backupString) {
      const backupData = JSON.parse(backupString);
      const validBackup = validateCartData(backupData);

      if (validBackup.length > 0) {
        console.log('🛒 Found cart backup with', validBackup.length, 'items');
        return validBackup;
      }
    }
  } catch (error) {
    console.error('🛒 Failed to recover from backup:', error);
  }

  return [];
}

/**
 * Get cart metadata
 * @returns {Object|null} - Cart metadata
 */
export function getCartMetadata() {
  try {
    const metadataString = window.localStorage.getItem(CART_METADATA_KEY);
    return metadataString ? JSON.parse(metadataString) : null;
  } catch (error) {
    console.error('🛒 Failed to load cart metadata:', error);
    return null;
  }
}

/**
 * Clear all cart data including backup
 */
export function clearLocalCart() {
  try {
    window.localStorage.removeItem(CART_KEY);
    window.localStorage.removeItem(CART_BACKUP_KEY);
    window.localStorage.removeItem(CART_METADATA_KEY);

    if (process.env.NODE_ENV === 'development') {
      console.log('🛒 All cart data cleared from localStorage');
    }
  } catch (error) {
    console.error('🛒 Failed to clear cart data:', error);
  }
}

/**
 * Get cart health status
 * @returns {Object} - Cart health information
 */
export function getCartHealth() {
  const cart = getLocalCart();
  const metadata = getCartMetadata();
  const hasBackup = !!window.localStorage.getItem(CART_BACKUP_KEY);

  return {
    itemsCount: cart.length,
    totalQuantity: cart.reduce((sum, item) => sum + (item.quantity || 1), 0),
    hasMetadata: !!metadata,
    hasBackup,
    lastSaved: metadata?.timestamp || null,
    version: metadata?.version || 'unknown',
    isHealthy: cart.every(isValidCartItem),
  };
}
